import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  Card,
  DialogActions,
  DialogContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  useTheme
} from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { useUploadImage } from "../../../../hooks/images";
import { ImageCache } from "../../../../models/ImageCache";
import { ImageDropzone } from "../../../ImageDropzone";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { ModifyFreeTextAttributeDialog } from "../../ModifyFreeTextAttributeDialog";
import { FreeTextListItem } from "../../../FreeTextListItem";
import { Firebase } from "../../../../services/Firebase";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";
import { useAddGearItem } from "../../../../hooks/gearItems/useAddGearItem";
import { useModifyGearItem } from "../../../../hooks/gearItems/useModifyGearItem";
import { useDeleteGearItem } from "../../../../hooks/gearItems/useDeleteGearItem";
import { IGearItem } from "../../../../hooks/gearItems";

export const ModifyGearItemDialog: FunctionComponent<IModifyGearItemDialogProps> = ({
  open,
  onClose,
  gearItem,
  originalImageData,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = gearItem ? strings.gear_item_title : strings.new_gear_item_title;
  const { addGearItem } = useAddGearItem();
  const { modifyGearItem } = useModifyGearItem();
  const { deleteGearItem } = useDeleteGearItem();
  const { uploadImage } = useUploadImage();
  const [imageFile, setImageFile] = useState<File>();
  const [isModifyProfileDialogOpen, setIsModifyProfileDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [didChangeImage, setDidChangeImage] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const genericImage = new URL("../../../../images/generic_image_placeholder.png", import.meta.url)
    .href;
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: gearItem?.name ?? "",
      description: gearItem?.description ?? "",
      brand: gearItem?.brand ?? "",
      gearItemStatus: gearItem?.status ?? "active"
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, description, brand, gearItemStatus }) => {
      setIsLoading(true);
      if (gearItem) {
        const { status, body } = await modifyGearItem({
          uuid: gearItem.uuid,
          name: name,
          hasImage: gearItem.hasImage,
          description: description,
          brand: brand,
          status: gearItemStatus
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_gear_item_action", { isNew: false });
          if (didChangeImage && imageFile && gearItem.uuid) {
            const newThumbnail = await uploadImage(
              { name: gearItem.uuid, file: imageFile },
              ImageCache.bigLabelImageSize,
              ImageCache.bigLabelImageSize,
              ImageCache.smallLabelImageSize,
              ImageCache.smallLabelImageSize
            );
            if (newThumbnail) {
              body.hasImage = true;
              body.thumbnail = newThumbnail;
            }
          }
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addGearItem({
          name: name,
          description: description,
          brand: brand,
          status: gearItemStatus
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_gear_item_action", { isNew: true });
          if (didChangeImage && imageFile && body.uuid) {
            const newThumbnail = await uploadImage(
              { name: body.uuid, file: imageFile },
              ImageCache.bigLabelImageSize,
              ImageCache.bigLabelImageSize,
              ImageCache.smallLabelImageSize,
              ImageCache.smallLabelImageSize
            );
            if (newThumbnail) {
              body.hasImage = true;
              body.thumbnail = newThumbnail;
            }
          }
          onNewItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!gearItem?.uuid) return;
    setIsLoading(true);
    const { status, body } = await deleteGearItem({ uuid: gearItem.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(gearItem);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (!open) return;
    setImageFile(undefined);
    setDidChangeImage(false);
  }, [open]);
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"sm"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {gearItem && (
            <ToolbarButton
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <Stack direction={"row"} spacing={2}>
            <Stack>
              <FieldTitle>{strings.gear_item_image_title}</FieldTitle>
              <ImageDropzone
                className={styles.dropzone}
                onImageChanged={file => {
                  setImageFile(file);
                  setDidChangeImage(true);
                }}
                placeholderImage={genericImage}
                initialImage={
                  originalImageData
                    ? `data:image/png;base64,${originalImageData}`
                    : gearItem?.thumbnail
                      ? `data:image/png;base64,${gearItem.thumbnail}`
                      : undefined
                }
                isLoading={false}
              />
            </Stack>
            <Stack sx={{ width: "100%" }}>
              <FieldTitle>{strings.gear_item_name_title}</FieldTitle>
              <div className={styles.textField}>
                <TextField
                  id="name"
                  name="name"
                  size="small"
                  autoFocus={!isMobile}
                  placeholder={strings.gear_item_name_placeholder}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  enterAction={formik.submitForm}
                />
              </div>
              <FieldTitle>{strings.gear_item_brand_title}</FieldTitle>
              <div className={styles.textField}>
                <TextField
                  id="brand"
                  name="brand"
                  size="small"
                  placeholder={strings.gear_item_brand_placeholder}
                  value={formik.values.brand}
                  onChange={formik.handleChange}
                  enterAction={formik.submitForm}
                />
              </div>
            </Stack>
          </Stack>
          <FieldTitle>{strings.description_title}</FieldTitle>
          <Card className={styles.profileCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyProfileDialogOpen(true)}
            />
          </Card>
          <FieldTitle>{strings.status_title}</FieldTitle>
          <ToggleButtonGroup
            value={formik.values.gearItemStatus}
            exclusive
            onChange={(_event, value) => {
              if (value !== null) {
                formik.setFieldValue("gearItemStatus", value).then();
              }
            }}
            sx={{ height: "30px", width: "100%" }}
          >
            <ToggleButton
              className={styles.toggleButton}
              sx={{ color: theme.palette.colors?.disabled }}
              value={"active"}
            >
              {strings.status_active}
            </ToggleButton>
            <ToggleButton
              className={styles.toggleButton}
              sx={{ color: theme.palette.colors?.disabled }}
              value={"inactive"}
            >
              {strings.status_inactive}
            </ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} disabled={isLoading} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <ModifyFreeTextAttributeDialog
        title={strings.description_title}
        description={strings.edit_description_description}
        placeholder={strings.description_title}
        value={formik.values.description}
        open={isModifyProfileDialogOpen}
        onClose={() => setIsModifyProfileDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </FormikProvider>
  );
};

export interface IModifyGearItemDialogProps {
  open: boolean;
  onClose: () => void;
  gearItem?: IGearItem;
  originalImageData?: string;
  onNewItem?: (gearItem: IGearItem) => void;
  onModifiedItem?: (gearItem: IGearItem) => void;
  onDeletedItem?: (gearItem: IGearItem) => void;
}
